import React, { useState, useCallback, useEffect } from 'react';
import TenantConfigService from '../../services/TenantConfigService';

/**
 * Time Blocks CSV Uploader
 * 
 * Access this page directly at: /admin/upload-time-blocks
 * Not included in main navigation - admin utility page only
 * 
 * Expected CSV format:
 * division, block_name, start_date, end_date, block_type, rotation_days, schedule_type_id, monday, tuesday, wednesday, thursday, friday
 * 
 * Example:
 * B, Fall Semester, 2025-08-18, 2025-12-19, weekly, 1, regular, regular, regular, schedule_1754168396006, regular, schedule_friday
 */

const TimeBlocksUploader = ({ currentUser }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [previewMode, setPreviewMode] = useState(true);
  const [selectedBlocks, setSelectedBlocks] = useState(new Set());
  const [parseError, setParseError] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [scheduleTypes, setScheduleTypes] = useState([]);

  const tenantId = currentUser?.tenantId;

  // Load existing divisions and schedule types for reference
  useEffect(() => {
    const loadData = async () => {
      if (!tenantId) return;
      
      try {
        const [divisionsConfig, scheduleTypesConfig] = await Promise.all([
          TenantConfigService.getConfig(tenantId, 'divisions', { divisions: [] }),
          TenantConfigService.getConfig(tenantId, 'schedule_types', { scheduleTypes: [] })
        ]);
        
        setDivisions(divisionsConfig.divisions || []);
        setScheduleTypes(scheduleTypesConfig.scheduleTypes || []);
      } catch (err) {
        console.error('Error loading config data:', err);
      }
    };
    
    loadData();
  }, [tenantId]);

  // Generate unique ID
  const generateId = () => `block_${Date.now()}${Math.random().toString(36).substr(2, 9)}`;

  // Parse CSV file
  const parseCSV = useCallback((text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSV must have a header row and at least one data row');
    }

    // Parse header
    const header = lines[0].split(',').map(h => h.trim().replace(/"/g, '').toLowerCase());
    console.log('📋 CSV Headers:', header);
    
    // Find column indices
    const divisionIdx = header.findIndex(h => 
      h === 'division_code' || h === 'division' || h === 'code'
    );
    const nameIdx = header.findIndex(h => 
      h === 'block_name' || h === 'name' || h === 'time_block_name' || h === 'pattern_name'
    );
    const startDateIdx = header.findIndex(h => 
      h === 'start_date' || h === 'startdate' || h === 'start'
    );
    const endDateIdx = header.findIndex(h => 
      h === 'end_date' || h === 'enddate' || h === 'end'
    );
    const blockTypeIdx = header.findIndex(h => 
      h === 'block_type' || h === 'blocktype' || h === 'type'
    );
    const rotationDaysIdx = header.findIndex(h => 
      h === 'rotation_days' || h === 'rotationdays' || h === 'rotation'
    );
    const scheduleTypeIdx = header.findIndex(h => 
      h === 'schedule_type_id' || h === 'scheduletypeid' || h === 'schedule_type' || h === 'default_schedule'
    );
    const mondayIdx = header.findIndex(h => h === 'monday' || h === 'mon');
    const tuesdayIdx = header.findIndex(h => h === 'tuesday' || h === 'tue');
    const wednesdayIdx = header.findIndex(h => h === 'wednesday' || h === 'wed');
    const thursdayIdx = header.findIndex(h => h === 'thursday' || h === 'thu');
    const fridayIdx = header.findIndex(h => h === 'friday' || h === 'fri');

    console.log('📋 Column indices:', { 
      divisionIdx, nameIdx, startDateIdx, endDateIdx, blockTypeIdx, 
      rotationDaysIdx, scheduleTypeIdx, mondayIdx, tuesdayIdx, wednesdayIdx, thursdayIdx, fridayIdx 
    });

    if (divisionIdx === -1) {
      throw new Error('CSV must have a "division_code" or "division" column');
    }
    if (startDateIdx === -1 || endDateIdx === -1) {
      throw new Error('CSV must have "start_date" and "end_date" columns');
    }

    // Parse data rows
    const timeBlocks = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      // Handle quoted values with commas
      const values = [];
      let current = '';
      let inQuotes = false;
      for (const char of line) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      console.log(`📋 Row ${i} values:`, values);

      const divisionCode = values[divisionIdx]?.replace(/"/g, '').trim();
      if (!divisionCode) continue;

      const startDate = values[startDateIdx]?.replace(/"/g, '').trim();
      const endDate = values[endDateIdx]?.replace(/"/g, '').trim();
      
      if (!startDate || !endDate) continue;

      // Get optional fields with defaults
      const blockName = nameIdx !== -1 ? values[nameIdx]?.replace(/"/g, '').trim() : '';
      const blockType = blockTypeIdx !== -1 ? values[blockTypeIdx]?.replace(/"/g, '').trim() : 'weekly';
      const rotationDays = rotationDaysIdx !== -1 ? parseInt(values[rotationDaysIdx]?.replace(/"/g, '').trim()) || 1 : 1;
      const scheduleTypeId = scheduleTypeIdx !== -1 ? values[scheduleTypeIdx]?.replace(/"/g, '').trim() : 'regular';
      
      // Day-specific schedule mappings (default to scheduleTypeId if not specified)
      const monday = mondayIdx !== -1 && values[mondayIdx] ? values[mondayIdx].replace(/"/g, '').trim() : scheduleTypeId;
      const tuesday = tuesdayIdx !== -1 && values[tuesdayIdx] ? values[tuesdayIdx].replace(/"/g, '').trim() : scheduleTypeId;
      const wednesday = wednesdayIdx !== -1 && values[wednesdayIdx] ? values[wednesdayIdx].replace(/"/g, '').trim() : scheduleTypeId;
      const thursday = thursdayIdx !== -1 && values[thursdayIdx] ? values[thursdayIdx].replace(/"/g, '').trim() : scheduleTypeId;
      const friday = fridayIdx !== -1 && values[fridayIdx] ? values[fridayIdx].replace(/"/g, '').trim() : scheduleTypeId;

      console.log(`📋 Row ${i} schedule mapping:`, { monday, tuesday, wednesday, thursday, friday });

      timeBlocks.push({
        _rowId: `${divisionCode}_${i}`,
        divisionCode,
        blockName: blockName || `Block ${i}`,
        startDate,
        endDate,
        blockType,
        rotationDays,
        scheduleTypeId,
        scheduleMapping: {
          Monday: monday,
          Tuesday: tuesday,
          Wednesday: wednesday,
          Thursday: thursday,
          Friday: friday
        }
      });
    }

    return timeBlocks;
  }, []);

  // Handle file selection
  const handleFileChange = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setParseError(null);
    setUploadStatus(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const blocks = parseCSV(event.target.result);
        console.log('📋 Parsed CSV blocks:', blocks);
        console.log('📋 Divisions found:', [...new Set(blocks.map(b => b.divisionCode))]);
        setParsedData(blocks);
        setSelectedBlocks(new Set(blocks.map(b => b._rowId)));
      } catch (err) {
        setParseError(err.message);
        setParsedData([]);
      }
    };
    reader.readAsText(selectedFile);
  }, [parseCSV]);

  // Toggle block selection
  const toggleBlock = (rowId) => {
    const newSelected = new Set(selectedBlocks);
    if (newSelected.has(rowId)) {
      newSelected.delete(rowId);
    } else {
      newSelected.add(rowId);
    }
    setSelectedBlocks(newSelected);
  };

  // Select/deselect all
  const toggleAll = () => {
    if (selectedBlocks.size === parsedData.length) {
      setSelectedBlocks(new Set());
    } else {
      setSelectedBlocks(new Set(parsedData.map(b => b._rowId)));
    }
  };

  // Upload to Firestore
  const handleUpload = async () => {
    console.log('🚀 handleUpload called!', { tenantId, selectedBlocks: selectedBlocks.size, parsedData: parsedData.length });
    
    if (!tenantId) {
      setUploadStatus({ type: 'error', message: 'No tenant ID found. Please log in again.' });
      return;
    }

    const blocksToUpload = parsedData.filter(b => selectedBlocks.has(b._rowId));
    console.log('📦 Blocks to upload:', blocksToUpload);
    
    if (blocksToUpload.length === 0) {
      setUploadStatus({ type: 'error', message: 'No time blocks selected for upload.' });
      return;
    }

    setUploading(true);
    setUploadStatus(null);

    try {
      // Get existing calendar_assignments config
      const existingConfig = await TenantConfigService.getConfig(tenantId, 'calendar_assignments', {});
      console.log('📅 Existing calendar config:', existingConfig);
      
      // Group blocks by division
      const blocksByDivision = {};
      for (const block of blocksToUpload) {
        if (!blocksByDivision[block.divisionCode]) {
          blocksByDivision[block.divisionCode] = [];
        }
        blocksByDivision[block.divisionCode].push(block);
      }
      
      console.log('📅 Blocks grouped by division:', blocksByDivision);
      console.log('📅 Division codes found:', Object.keys(blocksByDivision));

      // Build updated config
      const updatedConfig = { ...existingConfig };
      
      let totalAdded = 0;
      let totalSkipped = 0;
      
      for (const [divisionCode, blocks] of Object.entries(blocksByDivision)) {
        console.log(`📅 ========== Processing division: ${divisionCode} ==========`);
        console.log(`📅 Blocks for ${divisionCode}:`, blocks);
        console.log(`📅 Current config for ${divisionCode}:`, updatedConfig[divisionCode]);
        
        // Get or create division entry
        if (!updatedConfig[divisionCode]) {
          console.log(`📅 Creating NEW calendar entry for division: ${divisionCode}`);
          updatedConfig[divisionCode] = {
            patterns: [],
            specificDates: []
          };
        }
        
        // Ensure patterns array exists
        if (!Array.isArray(updatedConfig[divisionCode].patterns)) {
          console.log(`📅 Initializing patterns array for: ${divisionCode}`);
          updatedConfig[divisionCode].patterns = [];
        }
        
        // Ensure specificDates array exists (preserve existing data)
        if (!Array.isArray(updatedConfig[divisionCode].specificDates)) {
          updatedConfig[divisionCode].specificDates = [];
        }

        // Get the pattern name from the first block for this division
        // (All blocks in the same division should target the same pattern)
        const targetPatternName = blocks[0].blockName;
        console.log(`📅 Looking for pattern named: "${targetPatternName}" in division ${divisionCode}`);
        console.log(`📅 Available patterns:`, updatedConfig[divisionCode].patterns.map(p => p.name));
        
        // Priority 1: Find pattern by NAME (case-insensitive match)
        let pattern = updatedConfig[divisionCode].patterns.find(p => 
          p.name && p.name.toLowerCase() === targetPatternName.toLowerCase()
        );
        
        if (pattern) {
          console.log(`📅 ✅ Found pattern by name: "${pattern.name}"`);
        }
        
        // Priority 2: If not found by name, look for any time_blocks pattern
        if (!pattern) {
          pattern = updatedConfig[divisionCode].patterns.find(p => p.type === 'time_blocks');
          if (pattern) {
            console.log(`📅 Found time_blocks pattern: "${pattern.name}"`);
          }
        }

        // Priority 3: If still not found, use the first pattern and convert it
        if (!pattern && updatedConfig[divisionCode].patterns.length > 0) {
          console.log(`📅 Converting first pattern to time_blocks for: ${divisionCode}`);
          pattern = updatedConfig[divisionCode].patterns[0];
          pattern.type = 'time_blocks';
          if (!Array.isArray(pattern.timeBlocks)) {
            pattern.timeBlocks = [];
          }
        }

        // Priority 4: Create new pattern only if none exist
        if (!pattern) {
          console.log(`📅 Creating new pattern "${targetPatternName}" for division: ${divisionCode}`);
          pattern = {
            id: `pattern_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
            name: targetPatternName || 'Main Schedule',
            description: 'Created from CSV import',
            type: 'time_blocks',
            rotationDays: 10,
            startDate: blocks[0].startDate,
            scheduleMapping: {},
            timeBlocks: []
          };
          updatedConfig[divisionCode].patterns.push(pattern);
        } else {
          console.log(`📅 Using existing pattern "${pattern.name}" for division: ${divisionCode}`, pattern);
          // Existing pattern - ensure it has timeBlocks array and is type time_blocks
          pattern.type = 'time_blocks';
          if (!Array.isArray(pattern.timeBlocks)) {
            pattern.timeBlocks = [];
          }
        }

        // Add new time blocks (checking for duplicates)
        for (const block of blocks) {
          // Check if a block with the same name AND date range already exists
          const isDuplicate = pattern.timeBlocks.some(existing => 
            existing.name === block.blockName && 
            existing.startDate === block.startDate && 
            existing.endDate === block.endDate
          );
          
          if (isDuplicate) {
            console.log(`📅 Skipping duplicate block: ${block.blockName} (${block.startDate} - ${block.endDate})`);
            totalSkipped++;
            continue;
          }
          
          console.log('📋 Block being added:', block);
          console.log('📋 Block scheduleMapping:', block.scheduleMapping);
          
          const newTimeBlock = {
            id: generateId(),
            name: block.blockName,
            blockType: block.blockType,
            startDate: block.startDate,
            endDate: block.endDate,
            rotationDays: block.rotationDays,
            scheduleTypeId: block.scheduleTypeId,
            scheduleMapping: block.scheduleMapping
          };
          
          console.log('📋 newTimeBlock:', newTimeBlock);
          
          pattern.timeBlocks.push(newTimeBlock);
          totalAdded++;
        }
      }
      
      console.log('📅 Final config to save:', JSON.stringify(updatedConfig, null, 2));

      // Save using TenantConfigService
      const success = await TenantConfigService.updateConfig(tenantId, 'calendar_assignments', updatedConfig);
      console.log('📅 Save result:', success);

      if (success) {
        let message = `Successfully uploaded ${totalAdded} time block(s) for ${Object.keys(blocksByDivision).length} division(s)!`;
        if (totalSkipped > 0) {
          message += ` (${totalSkipped} duplicate(s) skipped)`;
        }
        setUploadStatus({
          type: 'success',
          message
        });
      } else {
        throw new Error('Update returned false');
      }

    } catch (err) {
      console.error('❌ Upload error:', err);
      setUploadStatus({
        type: 'error',
        message: `Upload failed: ${err.message}`
      });
    } finally {
      setUploading(false);
    }
  };

  // Get unique divisions from parsed data
  const uniqueDivisions = [...new Set(parsedData.map(b => b.divisionCode))];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📅 Time Blocks Uploader</h1>
        <p style={styles.subtitle}>
          Step 4: Upload CSV file to bulk import calendar time blocks into Relief Roster
        </p>
      </div>

      {/* Order of Operations */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>📋 Upload Order of Operations</h3>
        <div style={styles.orderList}>
          <div style={styles.orderItem}>
            <span style={styles.orderNumber}>1</span>
            <span>Divisions → <code>/admin/upload-divisions</code></span>
            <span style={styles.checkMark}>✓</span>
          </div>
          <div style={styles.orderItem}>
            <span style={styles.orderNumber}>2</span>
            <span>Schedule Types → <code>/admin/upload-schedule-patterns</code></span>
            <span style={styles.checkMark}>✓</span>
          </div>
          <div style={styles.orderItem}>
            <span style={styles.orderNumber}>3</span>
            <span>Calendar Patterns → <code>/admin/upload-calendar-patterns</code></span>
            <span style={styles.checkMark}>✓</span>
          </div>
          <div style={{...styles.orderItem, ...styles.currentStep}}>
            <span style={{...styles.orderNumber, ...styles.currentNumber}}>4</span>
            <span><strong>Time Blocks → You are here</strong></span>
            <span style={styles.arrow}>←</span>
          </div>
        </div>
      </div>

      {/* Reference Info */}
      {(divisions.length > 0 || scheduleTypes.length > 0) && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>📚 Available References</h3>
          <div style={styles.refGrid}>
            {divisions.length > 0 && (
              <div style={styles.refSection}>
                <strong>Divisions ({divisions.length}):</strong>
                <div style={styles.refList}>
                  {divisions.slice(0, 20).map(d => (
                    <span key={d.code} style={styles.refBadge}>{d.code}</span>
                  ))}
                  {divisions.length > 20 && <span style={styles.refMore}>+{divisions.length - 20} more</span>}
                </div>
              </div>
            )}
            {scheduleTypes.length > 0 && (
              <div style={styles.refSection}>
                <strong>Schedule Types ({scheduleTypes.length}):</strong>
                <div style={styles.refList}>
                  {scheduleTypes.slice(0, 10).map(s => (
                    <span key={s.id} style={styles.refBadgeAlt}>{s.id}</span>
                  ))}
                  {scheduleTypes.length > 10 && <span style={styles.refMore}>+{scheduleTypes.length - 10} more</span>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>📋 CSV Format Requirements</h3>
        <div style={styles.instructions}>
          <p>Your CSV file should have the following columns:</p>
          <code style={styles.codeBlock}>
division, block_name, start_date, end_date, block_type, rotation_days, schedule_type_id, monday, tuesday, wednesday, thursday, friday
          </code>
          <div style={styles.fieldList}>
            <p><strong>Required:</strong></p>
            <ul>
              <li><code>division</code> - Division code (e.g., "B", "EC", "HS")</li>
              <li><code>start_date</code> - Start date (YYYY-MM-DD format)</li>
              <li><code>end_date</code> - End date (YYYY-MM-DD format)</li>
            </ul>
            <p><strong>Optional:</strong></p>
            <ul>
              <li><code>block_name</code> - Name for the time block (e.g., "Fall Semester") - <strong>MUST match an existing Calendar Pattern name!</strong></li>
              <li><code>block_type</code> - "weekly" or "rotating" (default: "weekly")</li>
              <li><code>rotation_days</code> - Number of rotation days (default: 1)</li>
              <li><code>schedule_type_id</code> - Default schedule type ID (e.g., "regular")</li>
              <li><code>monday</code> through <code>friday</code> - Day-specific schedule type IDs</li>
            </ul>
          </div>
          <p style={{ marginTop: '12px', padding: '8px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffeaa7' }}>
            <strong>⚠️ Important:</strong> The <code>block_name</code> column should match the name of an existing Calendar Pattern created in Step 3. 
            This tells the uploader which pattern to add the time block to.
          </p>
          <p style={{ marginTop: '12px' }}><strong>Example:</strong></p>
          <code style={styles.codeBlock}>
{`division,block_name,start_date,end_date,block_type,rotation_days,schedule_type_id,monday,tuesday,wednesday,thursday,friday
B,TEST Semester,2025-08-18,2025-12-19,weekly,1,regular,regular,regular,schedule_1754168396006,regular,schedule_friday
EC,Main Schedule,2025-08-18,2025-10-31,weekly,1,regular,regular,regular,regular,regular,schedule_1754168396006`}
          </code>
        </div>
      </div>

      {/* File Upload */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>📤 Select File</h3>
        <div style={styles.uploadArea}>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={styles.fileInput}
            id="csv-upload"
          />
          <label htmlFor="csv-upload" style={styles.uploadLabel}>
            {file ? (
              <>
                <span style={styles.fileName}>✅ {file.name}</span>
                <span style={styles.fileSize}>({(file.size / 1024).toFixed(1)} KB)</span>
              </>
            ) : (
              <>
                <span style={styles.uploadIcon}>📁</span>
                <span>Click to select CSV file or drag and drop</span>
              </>
            )}
          </label>
        </div>

        {parseError && (
          <div style={styles.errorMessage}>
            ❌ {parseError}
          </div>
        )}
      </div>

      {/* Preview */}
      {parsedData.length > 0 && (
        <div style={styles.card}>
          <div style={styles.previewHeader}>
            <h3 style={styles.cardTitle}>
              👁️ Preview ({parsedData.length} time blocks for {uniqueDivisions.length} divisions)
            </h3>
            <div style={styles.previewActions}>
              <button onClick={toggleAll} style={styles.selectAllBtn}>
                {selectedBlocks.size === parsedData.length ? 'Deselect All' : 'Select All'}
              </button>
              <span style={styles.selectedCount}>
                {selectedBlocks.size} selected
              </span>
            </div>
          </div>

          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Select</th>
                  <th style={styles.th}>Division</th>
                  <th style={styles.th}>Block Name (Pattern)</th>
                  <th style={styles.th}>Dates</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Schedule Mapping</th>
                </tr>
              </thead>
              <tbody>
                {parsedData.slice(0, previewMode ? 20 : parsedData.length).map((block) => (
                  <tr key={block._rowId} style={styles.tr}>
                    <td style={styles.td}>
                      <input
                        type="checkbox"
                        checked={selectedBlocks.has(block._rowId)}
                        onChange={() => toggleBlock(block._rowId)}
                        style={styles.checkbox}
                      />
                    </td>
                    <td style={styles.td}>
                      <code style={styles.divisionCode}>{block.divisionCode}</code>
                    </td>
                    <td style={styles.td}>{block.blockName}</td>
                    <td style={styles.td}>
                      <span style={styles.dateRange}>
                        {block.startDate} → {block.endDate}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.typeBadge}>
                        {block.blockType} ({block.rotationDays}d)
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.scheduleMapping}>
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => (
                          <span key={day} style={styles.dayMapping} title={day}>
                            <span style={styles.dayLabel}>{day.substring(0, 2)}</span>
                            <span style={styles.dayValue}>{block.scheduleMapping[day]}</span>
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {parsedData.length > 20 && (
            <button
              onClick={() => setPreviewMode(!previewMode)}
              style={styles.showMoreBtn}
            >
              {previewMode ? `Show all ${parsedData.length} time blocks` : 'Show less'}
            </button>
          )}
        </div>
      )}

      {/* Upload Button */}
      {parsedData.length > 0 && (
        <div style={styles.uploadSection}>
          <button
            onClick={handleUpload}
            disabled={uploading || selectedBlocks.size === 0}
            style={{
              ...styles.uploadBtn,
              ...(uploading || selectedBlocks.size === 0 ? styles.uploadBtnDisabled : {})
            }}
          >
            {uploading ? (
              <>⏳ Uploading...</>
            ) : (
              <>🚀 Upload {selectedBlocks.size} Time Blocks to Firestore</>
            )}
          </button>

          {uploadStatus && (
            <div style={{
              ...styles.statusMessage,
              ...(uploadStatus.type === 'success' ? styles.successMessage : styles.errorMessage)
            }}>
              {uploadStatus.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '16px',
    color: '#718096',
    margin: 0,
  },
  card: {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e2e8f0',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2d3748',
    margin: '0 0 16px 0',
  },
  orderList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  orderItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    background: '#f7fafc',
    borderRadius: '8px',
    fontSize: '14px',
  },
  currentStep: {
    background: '#ebf8ff',
    border: '2px solid #4299e1',
  },
  orderNumber: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: '#e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '14px',
    color: '#4a5568',
  },
  currentNumber: {
    background: '#4299e1',
    color: '#fff',
  },
  checkMark: {
    marginLeft: 'auto',
    color: '#48bb78',
    fontWeight: '700',
  },
  arrow: {
    marginLeft: 'auto',
    color: '#4299e1',
    fontWeight: '700',
    fontSize: '18px',
  },
  refGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  refSection: {
    fontSize: '14px',
  },
  refList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '6px',
  },
  refBadge: {
    background: '#e6fffa',
    color: '#234e52',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
  },
  refBadgeAlt: {
    background: '#ebf8ff',
    color: '#2b6cb0',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
  },
  refMore: {
    color: '#718096',
    fontSize: '12px',
    fontStyle: 'italic',
  },
  instructions: {
    fontSize: '14px',
    color: '#4a5568',
    lineHeight: '1.6',
  },
  fieldList: {
    marginTop: '12px',
    fontSize: '13px',
  },
  codeBlock: {
    display: 'block',
    background: '#f7fafc',
    padding: '12px 16px',
    borderRadius: '6px',
    fontSize: '11px',
    color: '#2d3748',
    overflowX: 'auto',
    border: '1px solid #e2e8f0',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  uploadArea: {
    position: 'relative',
  },
  fileInput: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: 'pointer',
  },
  uploadLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    border: '2px dashed #cbd5e0',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: '#718096',
    fontSize: '14px',
    gap: '8px',
  },
  uploadIcon: {
    fontSize: '32px',
  },
  fileName: {
    color: '#2b6cb0',
    fontWeight: '600',
  },
  fileSize: {
    color: '#a0aec0',
    fontSize: '12px',
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
  },
  previewActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  selectAllBtn: {
    padding: '6px 12px',
    fontSize: '13px',
    background: '#edf2f7',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#4a5568',
  },
  selectedCount: {
    fontSize: '13px',
    color: '#718096',
  },
  tableContainer: {
    overflowX: 'auto',
    marginTop: '16px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: '2px solid #e2e8f0',
    color: '#4a5568',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  tr: {
    borderBottom: '1px solid #f0f0f0',
  },
  td: {
    padding: '10px 12px',
    verticalAlign: 'middle',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  divisionCode: {
    background: '#e6fffa',
    color: '#234e52',
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  dateRange: {
    fontSize: '12px',
    color: '#4a5568',
    whiteSpace: 'nowrap',
  },
  typeBadge: {
    background: '#fef3c7',
    color: '#92400e',
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '11px',
  },
  scheduleMapping: {
    display: 'flex',
    gap: '4px',
    flexWrap: 'wrap',
  },
  dayMapping: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: '#f7fafc',
    borderRadius: '4px',
    padding: '2px 6px',
    minWidth: '50px',
  },
  dayLabel: {
    fontSize: '9px',
    color: '#718096',
    fontWeight: '600',
  },
  dayValue: {
    fontSize: '10px',
    color: '#2d3748',
    maxWidth: '60px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  showMoreBtn: {
    marginTop: '16px',
    padding: '8px 16px',
    background: 'transparent',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    color: '#4a5568',
    cursor: 'pointer',
    fontSize: '13px',
  },
  uploadSection: {
    textAlign: 'center',
    padding: '20px 0',
  },
  uploadBtn: {
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '600',
    background: '#0891b2',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  uploadBtnDisabled: {
    background: '#a0aec0',
    cursor: 'not-allowed',
  },
  statusMessage: {
    marginTop: '16px',
    padding: '12px 20px',
    borderRadius: '8px',
    fontSize: '14px',
  },
  successMessage: {
    background: '#f0fff4',
    color: '#276749',
    border: '1px solid #9ae6b4',
  },
  errorMessage: {
    background: '#fff5f5',
    color: '#c53030',
    border: '1px solid #feb2b2',
    marginTop: '12px',
    padding: '12px',
    borderRadius: '6px',
    fontSize: '14px',
  },
};

export default TimeBlocksUploader;